import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { filter } from 'rxjs/operators'
import { TranslateService } from '@ngx-translate/core'

import { GoRealtimeClient } from '@core/services/realtime/go-realtime.client'
import { WebAlert } from '@core/modules/toolbar/notifications/web-alert'
import { NotificationMessage } from '@core/modules/toolbar/notifications/notification-message.model'
import { ApiService } from '@shared/services/api.service'
import { withoutErrorPageRedirect } from '@app/core/interceptors/error-redirect-context'
import { SnackBarService } from '@core/modules/snack-bar/snack-bar.service'
import { SettingsService } from '@app/shared/common-components/settings/settings.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { EventLog } from '@shared/modules/alerts/shared/event-log.model'
import { Vehicle } from '@shared/models/vehicle.model'
import { NotificationAcknowledgeStatus } from '@shared/models/notification-acknowledge.model'

import { SosAlert, SOS_EVENT_NAME } from './sos-alert.model'
import { mapEventLogToSos } from './sos-event-log'

/**
 * Persistent SOS/panic surface. Root singleton, first injected by ToolbarComponent (post-login),
 * so the JWT is available. Live panic frames arrive over the realtime v3 native WS: the shared
 * GoRealtimeClient demuxes them onto `alert$` as flat WebAlerts carrying the `event` name, so we
 * filter `event === 'PANIC_BUTTON'` there (RealtimeAlertService.normalizeWebAlert drops the name).
 */
@Injectable({ providedIn: 'root' })
export class SosService {
  private readonly _alerts$ = new BehaviorSubject<SosAlert[]>([])
  readonly alerts$: Observable<SosAlert[]> = this._alerts$.asObservable()

  // Emits the id of the bell notification behind an SOS when that SOS is acknowledged, so the toolbar /
  // topbar can mark the matching notification read (John: acknowledging an SOS should also clear it from
  // the bell). The SOS alert id and its notification id share the same source id (WebAlert / message id).
  private readonly _notificationRead$ = new Subject<string>()
  readonly notificationRead$: Observable<string> = this._notificationRead$.asObservable()

  private soundEnabled = false
  // SOS is a critical alert: a reused element playing the dedicated alarm tone once per new alert.
  // Primed on the first user gesture so the browser autoplay policy cannot silence a socket-pushed
  // alert that has no gesture of its own.
  private alarmAudio?: HTMLAudioElement

  get alerts(): SosAlert[] {
    return this._alerts$.value
  }

  constructor(
    private readonly realtimeClient: GoRealtimeClient,
    private readonly apiService: ApiService,
    private readonly snackBar: SnackBarService,
    private readonly translate: TranslateService,
    private readonly settingsService: SettingsService,
    private readonly authService: AuthService,
  ) {
    this.listenForSos()
    this.loadActive()
    this.hydrateSoundSetting()
    this.initAlarmAudio()
  }

  // Public so a synthetic alert can be pushed in dev/testing; production callers use the socket.
  ingest(raw: WebAlert): void {
    if (raw?.event !== SOS_EVENT_NAME) return
    const alert = this.fromWebAlert(raw)
    if (this._alerts$.value.some((a) => a.id === alert.id)) return
    this._alerts$.next([alert, ...this._alerts$.value])
    this.enrich(alert)
    this.resolveVehicle(alert)
    this.playAlarm()
  }

  acknowledge(id: number, note = ''): void {
    const alert = this._alerts$.value.find((a) => a.id === id)
    if (!alert?.acknowledgeId) {
      this.remove(id) // no acknowledge record => clear locally only
      this._notificationRead$.next(String(id))
      return
    }
    this.apiService
      .post(`/notifications/acknowledge/${alert.acknowledgeId}`, {
        status: NotificationAcknowledgeStatus.Actioned,
        comment: note,
      })
      .subscribe({
        next: () => {
          this.remove(id)
          this._notificationRead$.next(String(id))
        },
        error: () => this.snackBar.notificationArrived(this.translate.instant('SOS.ACK_FAILED') as string),
      })
  }

  private listenForSos(): void {
    this.realtimeClient.connect() // idempotent — ensures the shared WS is up even without the map/bell
    this.realtimeClient.alert$.pipe(filter((raw: WebAlert) => raw?.event === SOS_EVENT_NAME)).subscribe((raw: WebAlert) => this.ingest(raw))
  }

  private loadActive(): void {
    // L30-2705: background load on app start; the live socket covers a failure, so no error page.
    const context = withoutErrorPageRedirect()
    const page$ = this.apiService.getPageable<NotificationMessage>(
      '/notifications/messages',
      1,
      100,
      undefined,
      undefined,
      undefined,
      undefined,
      context,
    )
    page$.subscribe({
      next: ({ data }) => {
        const active = (data || [])
          .filter((m) => m?.event?.eventName === SOS_EVENT_NAME)
          .filter((m) => this.isPendingAck(m))
          .map((m) => this.fromNotification(m))
        if (!active.length) return
        const existing = new Set(this._alerts$.value.map((a) => a.id))
        const fresh = active.filter((a) => !existing.has(a.id))
        if (!fresh.length) return
        this._alerts$.next([...fresh, ...this._alerts$.value])
        fresh.forEach((a) => {
          this.enrich(a)
          this.resolveVehicle(a)
        })
      },
      error: () => {
        /* live socket still populates */
      },
    })
  }

  private enrich(alert: SosAlert): void {
    if (!alert.eventLogId) return
    this.apiService.getPageable<EventLog>('/event-log/json', 1, 1, undefined, { id: alert.eventLogId }).subscribe({
      next: ({ data }) => {
        const eventLog = (data || [])[0]
        if (!eventLog) return
        const patch = Object.fromEntries(
          Object.entries(mapEventLogToSos(eventLog)).filter(([, value]) => value != null),
        ) as Partial<SosAlert>
        this._alerts$.next(this._alerts$.value.map((a) => (a.id === alert.id ? { ...a, ...patch } : a)))
      },
      error: () => {
        /* keep notification-derived fields */
      },
    })
  }

  private resolveVehicle(alert: SosAlert): void {
    if (alert.vehicleId != null) return
    const name = alert.vehicle?.trim()
    if (!name) return
    const fields = ['regNo', 'defaultLabel', 'device.trackerData']
    this.apiService.getPageable<Vehicle>('/vehicles/fields', 0, 10, undefined, { regNo: name }, fields).subscribe({
      next: ({ data }) => {
        const vehicles = data || []
        const exactMatch = vehicles.find((v) => v.regNo === name || v.defaultLabel === name)
        const match = exactMatch ?? (vehicles.length === 1 ? vehicles[0] : undefined)
        if (!match) return
        const patch: Partial<SosAlert> = { vehicleId: match.id }
        const coordinates = match.device?.trackerData?.lastCoordinates
        if (coordinates?.lat != null && +coordinates.lat !== 0 && +coordinates.lng !== 0) {
          patch.lat = +coordinates.lat
          patch.lng = +coordinates.lng
        }
        this._alerts$.next(this._alerts$.value.map((a) => (a.id === alert.id ? { ...a, ...patch } : a)))
      },
      error: () => {
        /* leave vehicleId unset (button hidden) */
      },
    })
  }

  private remove(id: number): void {
    this._alerts$.next(this._alerts$.value.filter((a) => a.id !== id))
  }

  private isPendingAck(message: NotificationMessage): boolean {
    return !!message.acknowledge && message.acknowledge.status !== NotificationAcknowledgeStatus.Actioned
  }

  private fromWebAlert(raw: WebAlert): SosAlert {
    return {
      id: Number(raw.id),
      driver: raw.subject || 'SOS alert',
      vehicle: this.parseVehicleName(raw.subject, raw.body) ?? '',
      location: raw.body || '',
      occurrenceTime: this.toOccurrenceTime(raw.ts),
      message: raw.body,
      acknowledgeId: undefined, // live frame carries no acknowledge id
      eventId: raw.eventId ?? undefined,
      eventLogId: raw.eventLogId,
      eventTeam: raw.entityTeam?.type,
      vehicleId: raw.vehicleId,
    }
  }

  private fromNotification(message: NotificationMessage): SosAlert {
    return {
      id: Number(message.id),
      driver: message.subject || 'SOS alert',
      vehicle: this.parseVehicleName(message.subject, message.message) ?? '',
      location: message.message || '',
      occurrenceTime: message.occurrenceTime,
      message: message.message,
      acknowledgeId: message.acknowledge?.id,
      eventId: message.event?.eventId,
      eventLogId: message.event?.eventLogId,
      eventTeam: message.event?.entityTeam?.type,
      vehicleId: message.event?.vehicleId,
    }
  }

  private parseVehicleName(subject?: string, body?: string): string | undefined {
    const source = subject || body || ''
    const matched = source.match(/vehicle:\s*(.+?)\s*\.?\s*$/i)
    return matched?.[1]?.trim() || undefined
  }

  private toOccurrenceTime(ts: string | number | undefined): string {
    if (ts == null || ts === '') return ''
    return typeof ts === 'number' ? new Date(ts).toISOString() : ts
  }

  private hydrateSoundSetting(): void {
    const userId = this.authService.getCurrentUser()?.id
    if (!userId) return
    this.settingsService.getUserNotificationSoundSetting(userId).subscribe((setting) => (this.soundEnabled = !!setting?.value))
  }

  // Build the SOS alarm element. SOS uses its own dedicated alarm tone (not the notification chime),
  // played once per new alert, because it is a critical alert.
  //
  // Browsers block programmatic audio until the page has had a user gesture, and a socket-pushed SOS
  // has no gesture of its own. So unlock the element on the first interaction (a muted play then pause
  // inside the gesture marks it user-activated); after that playAlarm() is allowed with no fresh gesture.
  private initAlarmAudio(): void {
    this.alarmAudio = new Audio('/assets/audio/sos_alarm.mp3')
    this.alarmAudio.volume = 1
    this.alarmAudio.load()

    const unlock = (): void => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)

      const audio = this.alarmAudio
      if (!audio) return

      audio.muted = true
      audio
        .play()
        .then(() => {
          audio.pause()
          audio.currentTime = 0
          audio.muted = false
        })
        .catch(() => {
          audio.muted = false
        })
    }

    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
  }

  // Play the SOS alarm for a NEW (live) SOS, respecting the user's mute setting (John, DT-230:
  // dedicated sound but do NOT override mute). Plays once per alert (no loop).
  private playAlarm(): void {
    if (!this.soundEnabled) return

    const audio = this.alarmAudio
    if (!audio) return

    audio.currentTime = 0
    audio.play().catch(() => {
      // Autoplay still blocked (no user gesture yet this session), nothing more to do.
    })
  }
}
